/**
 * RequireAppAccess — the gate every App route family renders behind.
 *
 * Two gates, both required (two-gate doctrine: entitlement = purchased,
 * module+flag = rolled out):
 *   1. The app's workspace module + individual flag, resolved through the
 *      canonical policy-aware gate (`useModuleGate` / the pure
 *      `resolveModuleGate` it wraps — build contract §2.7).
 *   2. The app's entitlement key on the workspace plan.
 *
 * Blocked users land on the suite launcher (/apps), which only lists the
 * apps they can open — absence, not disablement (C Mode rule 2).
 */

import { useEffect, useReducer, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useModuleGate } from '@/lib/moduleGate';
import {
  resolveModuleGate,
  useWorkspaceModulePolicies,
  useWorkspaceModules,
  useWorkspaceModulesReady,
  useWorkspaceType,
} from '@/lib/workspaceModulesService';
import { FEATURE_FLAGS_UPDATED_EVENT } from '@/lib/featureFlags';
import { defaultPlan, hasEntitlement, type WorkspacePlanRecord } from '@/lib/entitlements';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { isAdminRole } from '@/types/prioritization/roles';
import { APP_REGISTRY, type AppDefinition } from './registry';
import { offeredInWorkspace } from './appsHomeSections';
import { appTelemetry } from '@/lib/appTelemetry';

function useResolvedPlan(): WorkspacePlanRecord {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspacePlan = useTenantStore((s) => s.workspacePlan);
  return workspacePlan ?? defaultPlan(workspaceId ?? 'unknown');
}

/** Gate state for a single app (canonical hook path). */
export function useAppAccess(app: AppDefinition): { ready: boolean; allowed: boolean } {
  const ready = useWorkspaceModulesReady();
  const gateOpen = useModuleGate(app.moduleKey);
  const plan = useResolvedPlan();
  return { ready, allowed: gateOpen && hasEntitlement(plan, app.entitlementKey) };
}

/** All apps the current user can open, resolved through the pure canonical
 *  resolver so the registry can be filtered without hooks-in-a-loop. */
export function useAccessibleApps(): { ready: boolean; apps: AppDefinition[] } {
  const ready = useWorkspaceModulesReady();
  const features = useWorkspaceModules();
  const policies = useWorkspaceModulePolicies();
  const currentRole = useUserStore((s) => s.currentRole);
  const plan = useResolvedPlan();
  const workspaceType = useWorkspaceType();
  const [, bump] = useReducer((n: number) => n + 1, 0);

  // Personal flag overrides change out-of-band; re-render to keep gates live
  // (same subscription useModuleGate maintains).
  useEffect(() => {
    const onFlagsChanged = () => bump();
    window.addEventListener(FEATURE_FLAGS_UPDATED_EVENT, onFlagsChanged);
    return () => window.removeEventListener(FEATURE_FLAGS_UPDATED_EVENT, onFlagsChanged);
  }, []);

  const isAdmin = currentRole ? isAdminRole(currentRole) : false;
  const apps = APP_REGISTRY.filter(
    (app) =>
      // A retired app is on record but not offered; its links open its replacement.
      app.status !== 'retired' &&
      // An individual app is offered only in a personal workspace (its tiers, R18).
      offeredInWorkspace(app, workspaceType) &&
      resolveModuleGate(features, policies, app.moduleKey, isAdmin) &&
      hasEntitlement(plan, app.entitlementKey)
  );
  return { ready, apps };
}

export function RequireAppAccess({
  app,
  children,
}: {
  app: AppDefinition;
  children: ReactNode;
}) {
  const { ready, allowed } = useAppAccess(app);

  // FR-20 app.first_open — emitted once per (user, app), deduped server-side.
  // Fires only once access actually resolves, so a blocked user never counts
  // as having opened the app.
  useEffect(() => {
    if (ready && allowed) void appTelemetry.firstOpen(app.appId);
  }, [ready, allowed, app.appId]);

  // Avoid a redirect flash while workspace modules are still loading.
  if (!ready) return null;
  if (!allowed) return <Navigate to="/apps" replace />;
  return <>{children}</>;
}
